import type { ElementOrComponent } from '@findigs/helpers/src/helpers/polymorphic';
import { Link } from '@findigs/ui-kit/src/components/link';
import type { LinkProps } from '@findigs/ui-kit/src/components/link';
import { LinkIcon } from '@findigs/ui-kit/src/components/link-icon';
import type { LinkIconProps } from '@findigs/ui-kit/src/components/link-icon';
import { useCallback } from 'react';

export type FrameworkLinkProps = LinkProps &
  Partial<Omit<LinkIconProps, keyof LinkProps>> &
  Omit<React.HTMLProps<HTMLAnchorElement>, 'ref' | 'as' | 'href'> & {
    href?: string;
    isInternal?: boolean;
  };

export type UseFrameworkLinkProps<E extends ElementOrComponent> = {
  /**
   * The framework's built-in `Link` component to be used.
   *
   * If no frameworks are in use, use `a` (anchor tag).
   *
   * @example GatsbyLink, RemixLink, etc
   */
  frameworkLink: E;
  /**
   * The name of the prop that should be passed into the component
   * referenced in `frameworkLink` to specify the destination url.
   *
   * If no frameworks are in use, use `href`.
   * @example For GatsbyLink and RemixLink the prop would be 'to'
   */
  frameworkLinkProp: keyof React.ComponentPropsWithoutRef<E>;

  /**
   * Function that returns whether the framework's Link component should be used for
   * a given URL. If not, uses the vanilla `<a>` tag instead.
   *
   * Defaults to `(href: string) => href.startsWith('/')`
   */
  shouldUseFrameworkLink?: (href: string) => boolean;

  /**
   * Disabling styles causes this hook to not apply ui-kit's `Link` component styles
   * @default false
   */
  disableStyles?: boolean;
};

/**
 * A hook that can be used when building components that internally need to
 * use ui-kit's `Link` and/or `LinkIcon` while at the same time interfacing
 * with external framework-provided `Link` components.
 *
 * @returns A component that will render using either `LinkIcon` or `Link`
 * depending on whether an icon prop is provided, and forward its `href` prop
 * to the appropriate prop depending on the component passed into this hook.
 *
 * External links render internally using `<a>`, and internal links using
 * `frameworkLink`.
 */
export function useFrameworkLink<E extends ElementOrComponent>({
  frameworkLink,
  frameworkLinkProp,
  disableStyles = false,
  shouldUseFrameworkLink = (href) => href.startsWith('/'),
}: UseFrameworkLinkProps<E>) {
  const FrameworkLink = useCallback(
    ({ href, icon, children, isInternal, ...props }: FrameworkLinkProps) => {
      const As =
        href !== undefined &&
        (isInternal === true ||
          (isInternal == null && shouldUseFrameworkLink(href)))
          ? (frameworkLink as ElementOrComponent)
          : 'a';
      const prop = As === 'a' ? 'href' : frameworkLinkProp;

      if (disableStyles) {
        return icon ? (
          <As {...props} {...{ [prop]: href }}>
            {icon}
            <span>{children}</span>
          </As>
        ) : (
          <As {...props} {...{ [prop]: href }}>
            {children}
          </As>
        );
      } else {
        return icon ? (
          <LinkIcon icon={icon} as={As} {...props} {...{ [prop]: href }}>
            {children}
          </LinkIcon>
        ) : (
          <Link as={As} {...props} {...{ [prop]: href }}>
            {children}
          </Link>
        );
      }
    },
    [shouldUseFrameworkLink, frameworkLink, frameworkLinkProp, disableStyles]
  );

  return { FrameworkLink };
}
